import DateHelper from '../../Utility/DateHelper';
import { PlayerTask } from './PlayerTasks';
import { IRewardData, RewardType } from '../../Utility/RewardHelper';
import { IAudioData, IShowLevelAutoFlowPopupParams } from './PlayerActions';
import { TimerType } from '../../System/Timer/TimerTypes';
import { FTUXType } from "../../Player/PlayerProperty/Progress/FTUXType";
import {
    shopGiftReward,
} from '../../Constants/Economy';
import { FBTrackingEvent } from '../../Utility/Tracking/FBTrackingEvent';
import { HeavyUsersTracking } from '../../Utility/Tracking/TrackingHelpers/HeavyUsersTracking';
import { WorldDataHelper } from '../../Utility/WorldDataHelper';
import { isDynamicAILevel } from '../Level/LevelStateHelper';
import { LevelHelperSettings } from '../../Constants/Level';
import UIStateHelper from '../UI/UIStateHelper';
import { getCurrentLevelNumber, getHighestLevelCompleted } from './PlayerLevelHelper';
import { IGameState } from '../Game/IGameState';
import { IPlayerState, IPlayerStateTaskData } from './IPlayerState';
import { StarChestHelper } from '../../Utility/StarChestHelper';
import { Quests } from './QuestType';
import { TActionType } from '../../Utility/Tracking/DataSet/Transaction';
import DailyLoginHelper from '../../Utility/DailyLoginHelper';
import { ConditionalTrackingHelper } from '../../Utility/Tracking/TrackingHelpers/ConditionalTracking';
import { LoginStateHelper } from './Login/LoginStateHelper';
import { QuestStateHelper } from './Quests/QuestStateHelper';
import { StarChestStateHelper } from './StarChests/StarChestStateHelper';
import { ProgressStateHelper } from './Progress/ProgressStateHelper';
import { InventoryStateHelper } from './Inventory/InventoryStateHelper';
import { SocialStateHelper } from './Social/SocialStateHelper';
import TimerStateHelper from '../Timer/TimerStateHelper';
import { FTUXStateHelper } from '../Game/FTUX/FTUXStateHelper';
import { PopupID } from '../UI/PopupID';
import { LuckyWheelStateHelper } from './LuckyWheel/LuckyWheelStateHelper';
import { AdjustLevelEventsMap } from "../../Utility/Tracking/AdjustTracking";
import Game from "../../Game";
import { report } from '../../report';
import { FarmStateHelper } from './Farm/FarmStateHelper';
import { OnShowProps } from 'State/UI/UIActions';

export const SUNDAY_OFFER_DEBUG: boolean = false;

export namespace PlayerStateHelper {
    export const setDifficultyReduction = (
        oldState: IGameState,
        difficultyReduction: number
    ): IGameState => {
        const state = TimerStateHelper.setTimer(
            oldState,
            TimerType.DYNAMIC_HELPER_AI,
            DateHelper.now
        );
        return {
            ...state,
            playerState: {
                ...state.playerState,
                progress: {
                    ...state.playerState.progress,
                    difficultyReduction,
                },
            },
        };
    };

    /**
     * Shows either pre-level popup, star chest info or more levels coming soon popup
     */
    export const showLevelAutoFlowPopup = (
        state: IGameState,
        params: IShowLevelAutoFlowPopupParams = {}
    ): IGameState => {
        const levelNumber =
            params.levelNumber ?? getCurrentLevelNumber(state.playerState);
        const maxWorldLevel = WorldDataHelper.maxLevel(state.worldState.worldNumber);
        const highestLevelCompleted = getHighestLevelCompleted(state.playerState);
        const luckyWheelLevels = LuckyWheelStateHelper.getEligibleWheelLevels(state.playerState.lastClaimedSpinLevel, maxWorldLevel, highestLevelCompleted);

        if (levelNumber === state.playerState.petLevel) {
            return showPopup(PopupID.PRE_PET_MINIGAME_MENU, {});
        }

        if (PlayerStateHelper.shouldOpenChest(state.playerState, levelNumber)) {
            return showPopup(PopupID.STAR_CHEST_INFO, {levelNumber});
            // If there aren't any quests to collect then a level related popup can be shown
        }
        const hasPremiumSpins = (state.playerState.inventory.premium_spin ?? 0) > 0;
        if (
            // Ftux is completed
            FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.LUCKY_WHEEL) &&
            (
                // If player has premium spins or if levelNumber is a spin wheel level
                hasPremiumSpins ||
                (luckyWheelLevels.includes(levelNumber) && levelNumber > state.playerState.lastClaimedSpinLevel)
            )
        ) {
            return showPopup(PopupID.WHEEL, { premium: hasPremiumSpins});
        }

        if (
            params.considerMoreLevelsComingSoonPopup &&
            levelNumber === WorldDataHelper.maxLevel() &&
            ProgressStateHelper.getLevelStars(state, levelNumber) > 0
        ) {
            return showPopup(PopupID.MORE_LEVELS_COMING, {});
        }

        const dailyLoginTask = state.playerState.tasks.find(
            task => task.popup === PopupID.DAILY_LOGIN_MENU
        );
        if (
            LoginStateHelper.isDailyLoginUnlocked(state) && // If unlock requirement is met
            !FTUXStateHelper.isFtuxCompleted(
                state.playerState,
                FTUXType.DAILY_LOGIN
            ) && // If daily login ftux has not been completed
            !dailyLoginTask && // If there isn't a daily login task already
            DailyLoginHelper.canCollectDailyLoginReward(
                state.playerState
            ) // If reward can be collected (not sure if this is needed)
        ) {
            state = showPopup(PopupID.DAILY_LOGIN_MENU, {});
        }
        return showPopup(PopupID.LEVEL_POPUP, { levelNumber, fail: false });

        return state;

        function showPopup<T extends PopupID>(popupID: T, popupParams: OnShowProps<T>) {
            const alreadyQueued = state.playerState.tasks.some(task => task.task === PlayerTask.SHOW_POPUP && task.popup === popupID)
            if (alreadyQueued) return state;

            if (params.delay) {
                return {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(
                        state.playerState,
                        [
                            {
                                task: PlayerTask.SHOW_POPUP,
                                popup: popupID,
                                meta: popupParams,
                                delay: params.delay,
                            },
                        ]
                    ),
                };
            }
            return {
                ...state,
                UIState: UIStateHelper.showPopup(
                    state.UIState,
                    popupID,
                    popupParams
                ),
            };
        }
    };

    export const setPlayerState = (
        state: IGameState,
        playerState: IPlayerState
    ): IGameState => {
        return {
            ...state,
            playerState: playerState,
        };
    };

    export const checkTimers = (state: IGameState): IGameState => {
        const levelHelperDate: Date = state.playerState.timers.get(
            TimerType.DYNAMIC_HELPER_AI
        );

        // Clears dynamic match chance on login if past cooldown time
        if (
            levelHelperDate &&
            DateHelper.timeSince(levelHelperDate) >=
                LevelHelperSettings.idleTimeToResetDifficulty
        ) {
            state = PlayerStateHelper.setDifficultyReduction(state, 0);
        }

        return state;
    };

    export const clearTasks = (
        state: IPlayerState,
        tasksToClear?: IPlayerStateTaskData[]
    ): IPlayerState => {
        return {
            ...state,
            tasks: tasksToClear
                ? state.tasks.filter(task => !tasksToClear.includes(task))
                : [],
        };
    };

    export const addStateTaskDatas = (
        state: IPlayerState,
        taskDatas: Array<IPlayerStateTaskData>,
        queueNext?: boolean
    ): IPlayerState => {
        let tasks = queueNext
            ? [...taskDatas, ...state.tasks]
            : [...state.tasks, ...taskDatas];

        // Keeps tasks like next level popup at the end
        tasks = [
            ...tasks.filter(task => !isClosingTask(task)),
            ...tasks.filter(task => isClosingTask(task)),
        ];

        return {
            ...state,
            tasks,
        };
    }

    export const isClosingTask = (taskData: IPlayerStateTaskData): boolean => {
        return (
            taskData.task === PlayerTask.SHOW_POPUP &&
            (taskData.popup === PopupID.LEVEL_POPUP ||
                taskData.popup === PopupID.MORE_LEVELS_COMING ||
                taskData.popup === PopupID.STAR_CHEST_INFO)
        );
    }

    export const updatePlayerState = (
        oldState: IGameState,
        newPlayerState: IPlayerState
    ): IPlayerState => {
        let state = {
            ...oldState,
            playerState: {
                ...oldState.playerState,
                ...newPlayerState,
            }
        };
        state = { ...state, playerState: {
            ...state.playerState,
            maxWorld: Math.max(1, state.playerState.maxWorld)
        }};
        //state = PlayerStateHelper.updateTimers(state);
        state = QuestStateHelper.fillQuestsIfNeeded(state);
        return state.playerState;
    }

    export const levelComplete = (state: IGameState): IGameState => {
        const levelNumber: number = state.levelState.levelNumber;
        const {
            coins,
            stars,
            starsGained,
            stackReward,
        } = state.levelState.completeData;
        const levelData = ProgressStateHelper.getLevelData(
            state,
            levelNumber,
            state.levelState.levelType
        );
        const newLevelData = {
            levelNumber,
            stars:
                levelData.stars && levelData.stars > stars
                    ? levelData.stars
                    : stars,
            consecutiveUnsuccessfullAttempts: 0,
        };

        const previousStarCount = StarChestStateHelper.getStarChestStarCount(
            state.playerState
        );
        const starCountCap = StarChestHelper.getStarCountCap();
        if (
            state.playerState.starChestLevel !== null &&
            starsGained > 0 &&
            previousStarCount < starCountCap
        ) {
            state = {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(
                    state.playerState,
                    [
                        {
                            task: PlayerTask.STAR_CHEST_STAR_REWARD,
                            amount: starsGained,
                            previousStarCount,
                            currentStarChestTier: StarChestStateHelper.getStarChestUnlockTier(
                                state.playerState
                            ),
                            levelNumber: state.levelState.levelNumber,
                        },
                    ]
                ),
            };
        }

        if (starsGained > 0) {
            state = {
                ...state,
                playerState: QuestStateHelper.incrementQuestProgressBy(
                    state.playerState,
                    Quests.COLLECT_STARS,
                    starsGained
                ),
            };
        }

        if (isDynamicAILevel(state)) {
            state = PlayerStateHelper.setDifficultyReduction(
                state,
                levelData.consecutiveUnsuccessfullAttempts <
                    LevelHelperSettings.maxAttemptsToResetDifficulty
                    ? 0
                    : state.playerState.progress.difficultyReduction
            );
        }

        state = ProgressStateHelper.updateLevel(
            state,
            levelNumber,
            newLevelData,
            state.levelState.levelType
        );

        const firstComplete = !levelData.stars;
        if (firstComplete) {
            HeavyUsersTracking.trackHeavyUserLevelFirstCompleteIfNeeded(
                state.playerState
            );
            state = {
                ...state,
                playerState: QuestStateHelper.incrementQuestProgressBy(
                    state.playerState,
                    Quests.COMPLETE_LEVELS_1ST_TIME,
                    1,
                    TActionType.LEVEL_END
                ),
            };
        }

        state = ConditionalTrackingHelper.trackConditionally(state, [
            FBTrackingEvent.LVL_20_3DAYS,
            FBTrackingEvent.LVL_40_5DAYS,
            FBTrackingEvent.LVL_60_7DAYS,
            FBTrackingEvent.LVL_10_1DAY,
            FBTrackingEvent.LVL_10_2DAYS,
            FBTrackingEvent.LVL_10_3DAYS,
            FBTrackingEvent.LVL_15_1DAY,
            FBTrackingEvent.LVL_15_2DAYS,
            FBTrackingEvent.LVL_15_3DAYS,
            FBTrackingEvent.LVL_20_1DAY,
            FBTrackingEvent.LVL_20_2DAYS,
            FBTrackingEvent.LVL_20_5DAYS,
            FBTrackingEvent.LVL_20_7DAYS,
        ]);

        if (coins > 0) {
            state = {
                ...state,
                playerState: InventoryStateHelper.setCoins(
                    state.playerState,
                    state.playerState.inventory.coins + coins + stackReward
                ),
            };
        }

        if (
            levelNumber >=
                WorldDataHelper.maxLevel(state.playerState.maxWorld) &&
            state.playerState.maxWorld < WorldDataHelper.maxWorld()
        ) {
            state = {
                ...state,
                playerState: ProgressStateHelper.unlockNextWorld(
                    state.playerState
                ),
            };
        }

        state = QuestStateHelper.fillQuestsIfNeeded(state);

        const levelEvent = AdjustLevelEventsMap.get(state.levelState.levelNumber);
        if (levelEvent) {
            Game.sdk.gameTracking(levelEvent);
        }


        return state;
    }

    export const spinWheel = (state: IPlayerState, isPremium: boolean): IPlayerState => {
        state = isPremium ?
            { ...state, spunFirstDiamondWheel: true } :
            { ...state, spunFirstNormalWheel: true };

        if (getCurrentLevelNumber(state) !== state.lastClaimedSpinLevel && !isPremium) {
            state = PlayerStateHelper.setLastClaimedSpinLevel(state);
        }

        return state;
    }

    export const subscribeBotOptIn = (state: IPlayerState): IPlayerState =>{
        // Player got 500 coins in the first subscribe.
        if (state.progress.optInReward === 0) {
            state = {
                ...state,
                progress: {
                    ...state.progress,
                    optInReward: 1,
                },
            };
            state = InventoryStateHelper.setCoins(
                state,
                state.inventory.coins + 500
            );
        }

        return state;
    }

    export const completeWheelTimer = (oldState: IGameState): IGameState => {
        const state = TimerStateHelper.setTimer(
            oldState,
            TimerType.SPIN_WHEEL,
            null
        );
        return {
            ...state,
            playerState: {
                ...InventoryStateHelper.setWheelSpins(
                    state.playerState,
                    state.playerState.inventory.wheelSpins + 1
                ),
            },
        };
    }

    export const queueFTUXIfNeeded = (
        state: IGameState
    ): IGameState => {
        // TODO: Refactor queue methods to use full IGameState reference so it does not need to be exceptions e.g. FTUXStateHelper.queueEventFTUXIfNeeded()
        let {playerState} = state;

        playerState = StarChestStateHelper.queueStarChestFTUXIfNeeded(playerState);

        playerState = QuestStateHelper.queueQuestsFTUXIfNeeded(playerState);

        playerState = FarmStateHelper.queueFarmBtnFTUXIfNeeded(playerState);

        try {
            playerState = LuckyWheelStateHelper.queueWheelFTUXIfNeeded(playerState);
        } catch(err) {
            report("Failed to queue wheel FTUX", {
                sampleRate: 0.1,
                error: err
            });
        }

        // Make sure to prepare this state using `playerState` from above.
        state = FTUXStateHelper.queueEventFTUXIfNeeded({
            ...state,
            playerState
        });

        return state;
    }

    export const updateTimers = (state: IPlayerState): IPlayerState => {
        //state = this.updateCurrencySharePrizeTimer(state);
        //state = this.updateGiftTimer(state);
        state = SocialStateHelper.updateFriendTimers(state);

        //TODO: Other timers.

        return state;
    }

    export const updateGiftTimer = (playerState: IPlayerState): IPlayerState =>{
        // No timer set.
        if (playerState.timers.get(TimerType.GIFT) === null) {
            return playerState;
        }

        // Check if time is up.
        const now = DateHelper.now;
        const timeDiff =
            now.getTime() - playerState.timers.get(TimerType.GIFT).getTime();
        const twelveHours = DateHelper.hoursToMilliseconds(12);

        // One day has elapsed - remove the prize timer and reset all friends' gift cooldowns.
        if (timeDiff >= twelveHours) {
            const timers = new Map(playerState.timers);
            timers.set(TimerType.GIFT, null);
            return {
                ...playerState,
                timers,
                social: {
                    ...playerState.social,
                    friendData: playerState.social.friendData.map(friend => ({
                        ...friend,
                        giftCooldown: null,
                    })),
                },
            };
        }

        // Timer has not elapsed - no change.
        return playerState;
    }

    export const updateCurrencySharePrizeTimer = (
        playerState: IPlayerState
    ): IPlayerState => {
        // No timer set.
        if (playerState.timers.get(TimerType.SEND_GIFT_REQUEST_DATE) === null) {
            return playerState;
        }

        // Check if time is up.
        const now = DateHelper.now;
        const timeDiff =
            now.getTime() -
            playerState.timers.get(TimerType.SEND_GIFT_REQUEST_DATE).getTime();
        const oneDay = DateHelper.daysToMilliseconds(1);

        // One day has elapsed - remove the prize timer.
        if (timeDiff >= oneDay) {
            const timers = new Map(playerState.timers);
            timers.set(TimerType.SEND_GIFT_REQUEST_DATE, null);
            return {
                ...playerState,
                timers,
            };
        }

        // No change.
        return playerState;
    }

    export const setAudio = (
        state: IGameState,
        audioData: IAudioData
    ): IGameState => {
        state = { ...state };

        state.playerState.settings[audioData.type] = audioData.enabled;

        return state;
    }

    export const addFBTrackingEvent= (
        state: IGameState,
        event: FBTrackingEvent
    ): IGameState =>{
        return {
            ...state,
            playerState: {
                ...state.playerState,
                FBEvents: [...state.playerState.FBEvents, event],
            },
        };
    }

    export const claimShopGift = (oldState: IGameState): IGameState => {
        let state = {
            ...oldState,
            playerState: InventoryStateHelper.giveReward(
                oldState.playerState,
                shopGiftReward,
                { queueTask: true }
            ),
        };
        state = {
            ...state,
            playerState: ProgressStateHelper.setShopGiftUses(
                state.playerState,
                state.playerState.progress.shopGiftsCollected + 1
            ),
        };

        state = TimerStateHelper.setTimer(
            state,
            TimerType.SHOP_GIFT,
            DateHelper.now
        );

        return {
            ...state,
            playerState: {
                ...InventoryStateHelper.setShopGift(
                    state.playerState,
                    state.playerState.inventory.shopGift - 1
                ),
            },
        };
    }

    export const completeShopGiftTimer = (oldState: IGameState): IGameState => {
        const state = TimerStateHelper.setTimer(
            oldState,
            TimerType.SHOP_GIFT,
            null
        );

        return {
            ...state,
            playerState: {
                ...InventoryStateHelper.setShopGift(
                    state.playerState,
                    state.playerState.inventory.shopGift + 1
                ),
            },
        };
    }

    export const completeAdCooldownTimer = (oldState: IGameState): IGameState => {
        // Reset ads watched
        const state = ProgressStateHelper.setAdsWatched(oldState, 0);
        // Reset timer
        return TimerStateHelper.setTimer(
            state,
            TimerType.AD_COOLDOWN,
            null
        );
    }

    // TODO: Move from social into playerState now that its used for more than just social features
    export const getGBCUserID = (state: IPlayerState): string => {
        return state.social.userId;
    }

    export const setGBCUserID = (
        state: IPlayerState,
        userId: string
    ): IPlayerState => {
        return {
            ...state,
            social: {
                ...state.social,
                userId,
            },
        };
    }

    export const setIsNewUser = (state: IGameState): IGameState => {
        state = {
            ...state,
            perSessionData: {
                ...state.perSessionData,
                isNewUser: state.playerState.totalSessionCount <= 1,
            },
        };
        return state;
    }

    export const setLastClaimedSpinLevel = (state: IPlayerState): IPlayerState => {
        return {
            ...state,
            lastClaimedSpinLevel: getCurrentLevelNumber(state),
        }
    }

    export const shouldOpenChest = (_state: IPlayerState, _selectedLevel: number): boolean => {
        return Boolean(_state.starChestLevel) &&
            (
                getCurrentLevelNumber(_state) >= _state.starChestLevel &&
                _selectedLevel === _state.starChestLevel
            );
    }
}

// Move out of state helpers
export const scaleCoinRewardWithLevelCost = (
    reward: IRewardData,
    levelCost: number
): IRewardData => {
    if (reward.type === RewardType.COINS) {
        return { ...reward, value: Math.round(reward.value * levelCost) };
    } else {
        return reward;
    }
};
